import CompanyService from '../../services/company.js';
import { showNotification } from '../../utils.js';

export default class MessageStatisticsView {
    constructor(companyId) {
        this.companyId = companyId;
        this.container = document.createElement('div');
        this.container.className = 'p-6';
        this.chart = null;
        this.currentPeriod = 'day';

        this.formatMessageType = this.formatMessageType.bind(this);
    }

    formatMessageType(type) {
        switch (type) {
            case 'KASPI_DELIVERY':
                return 'Новый заказ';
            case 'ARCHIVE':
                return 'Заказ доставлен';
            default:
                return type;
        }
    }

    async render() {
        try {
            console.log('Fetching statistics for company:', this.companyId);
            const statistics = await CompanyService.getMessageStatistics(this.companyId, this.currentPeriod);
            console.log('Received statistics:', statistics);

            this.container.innerHTML = `
                <div class="space-y-6">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center space-x-4">
                            <h2 class="text-2xl font-bold">Статистика отправленных сообщений</h2>
                        </div>

						
                            <button id="back-to-companies" class="px-4 py-2 text-gray-600 hover:text-gray-800">
                                &#8656; Назад к компаниям
                            </button>
                    </div>

                    <!-- Time Period Selector -->
                    <div class="flex space-x-4 mb-4">
                        ${['day', 'week', 'month', 'year'].map(period => `
                            <button class="period-selector px-4 py-2 rounded ${this.currentPeriod === period ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}"
                                    data-period="${period}">
                                ${period === 'day' ? 'День' : period === 'week' ? 'Неделя' : period === 'month' ? 'Месяц' : 'Год'}
                            </button>
                        `).join('')}
                    
                        <button id="refresh-stats" class="px-4 py-2 bg-green-600 text-white rounded hover:bg-blue-700">
                            Обновить
                        </button>					
					
					</div>

                    <!-- Statistics Summary -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div class="bg-white p-4 rounded-lg shadow">
                            <h3 class="text-lg font-semibold text-gray-700">Всего сообщений</h3>
                            <p class="text-3xl font-bold text-blue-600">${statistics.summary.total_messages}</p>
                        </div>
                        ${Object.entries(statistics.summary.by_type).map(([type, count]) => `
                            <div class="bg-white p-4 rounded-lg shadow">
<h3 class="text-lg font-semibold text-gray-700">${this.formatMessageType(type)}</h3>
                                <p class="text-3xl font-bold text-blue-600">${count}</p>
                            </div>
                        `).join('')}
                    </div>

                    <!-- Chart -->
                    <div class="bg-white p-4 rounded-lg shadow mb-6">
                        <canvas id="statistics-chart" height="130"></canvas>
                    </div>

                    <!-- Detailed Table -->
                    <div class="bg-white shadow rounded-lg overflow-hidden">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Дата
                                    </th>
                                    ${(() => {
                                        const allStates = new Set();
                                        statistics.data.forEach(day => {
                                            Object.keys(day.stats).forEach(state => allStates.add(state));
                                        });
                                        return Array.from(allStates).map(type => `
<th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                ${this.formatMessageType(type)}
                                            </th>
                                        `).join('');
                                    })()}
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                ${statistics.data.map(day => {
                                    const allStates = new Set();
                                    statistics.data.forEach(d => {
                                        Object.keys(d.stats).forEach(state => allStates.add(state));
                                    });
                                    return `
                                        <tr>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                ${this.currentPeriod === 'day'
                                                    ? new Date(day.date).toLocaleString()
                                                    : this.currentPeriod === 'year'
                                                        ? new Date(day.date).toLocaleDateString([], { year: 'numeric', month: 'long' })
                                                        : new Date(day.date).toLocaleDateString()}
                                            </td>
                                            ${Array.from(allStates).map(state => `
                                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    ${day.stats[state] || 0}
                                                </td>
                                            `).join('')}
                                        </tr>
                                    `;
                                }).join('')}
                                ${statistics.data.length === 0 ? `
                                    <tr>
                                        <td colspan="${(() => {
                                            const allStates = new Set();
                                            statistics.data.forEach(day => {
                                                Object.keys(day.stats).forEach(state => allStates.add(state));
                                            });
                                            return allStates.size + 1; // +1 for the Date column
                                        })()}" class="px-6 py-4 text-sm text-gray-500 text-center">
                                            Сообщения еще не отправлялись
                                        </td>
                                    </tr>
                                ` : ''}
                            </tbody>
                        </table>
                    </div>
                </div>
            `;

            this.setupEventListeners();
            this.initChart(statistics);
        } catch (error) {
            showNotification(error.message, 'error');
            this.container.innerHTML = `
                <div class="text-center text-red-600">
                    Не удалось загрузить статистику сообщений
                </div>
            `;
        }
    }

    formatDateLabel(dateStr) {
        const date = new Date(dateStr);
        switch (this.currentPeriod) {
            case 'day':
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            case 'week':
            case 'month':
                return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
            case 'year':
                return date.toLocaleDateString([], { month: 'short', year: '2-digit' });
            default:
                return date.toLocaleDateString();
        }
    }

    initChart(statistics) {
        const ctx = this.container.querySelector('#statistics-chart').getContext('2d');
        
        // Destroy existing chart if it exists
        if (this.chart) {
            this.chart.destroy();
        }

        // Collect all unique order states
        const allOrderStates = new Set();
        statistics.data.forEach(day => {
            Object.keys(day.stats).forEach(state => allOrderStates.add(state));
        });

const datasets = Array.from(allOrderStates).map((type, index) => ({
            label: this.formatMessageType(type),
            data: statistics.data.map(day => day.stats[type] || 0),
            borderColor: this.getChartColor(index),
            backgroundColor: this.getChartColor(index, 0.1),
            tension: 0.4
        }));

        // Get appropriate title based on period
        const getTitleText = () => {
            switch (this.currentPeriod) {
                case 'day':
                    return 'Статистика сообщений по часам';
                case 'week':
                    return 'Статистика сообщений по дням (Последняя неделя)';
                case 'month':
                    return 'Статистика сообщений по дням (Последний месяц)';
                case 'year':
                    return 'Статистика сообщений по месяцам';
                default:
                    return 'Статистика сообщений за период';
            }
        };

        this.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: statistics.data.map(day => this.formatDateLabel(day.date)),
                datasets
            },
            options: {
                responsive: true,
                interaction: {
                    intersect: false,
                    mode: 'index'
                },
                plugins: {
                    legend: {
                        position: 'top'
                    },
                    title: {
                        display: true,
                        text: getTitleText()
                    }
                },
                scales: {
                    x: {
                        grid: {
                            display: true
                        },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 45
                        }
                    },
                    y: {
                        beginAtZero: true,
                        ticks: {
                            precision: 0
                        }
                    }
                }
            }
        });
    }

    getChartColor(index, alpha = 1) {
        const colors = [
            `rgba(59, 130, 246, ${alpha})`,   // Blue
            `rgba(16, 185, 129, ${alpha})`,   // Green
            `rgba(245, 158, 11, ${alpha})`,   // Yellow
            `rgba(239, 68, 68, ${alpha})`,    // Red
            `rgba(139, 92, 246, ${alpha})`    // Purple
        ];
        return colors[index % colors.length];
    }

    setupEventListeners() {
        const refreshButton = this.container.querySelector('#refresh-stats');
        if (refreshButton) {
            refreshButton.addEventListener('click', () => this.render());
        }

        const backButton = this.container.querySelector('#back-to-companies');
        if (backButton) {
            backButton.addEventListener('click', () => {
                window.location.hash = '#/companies';
            });
        }

        // Period selector buttons
        const periodButtons = this.container.querySelectorAll('.period-selector');
        periodButtons.forEach(button => {
            button.addEventListener('click', async () => {
                this.currentPeriod = button.dataset.period;
                await this.render();
            });
        });
    }

    getContainer() {
        return this.container;
    }
}
